/**
 * Client-side Page Designer V1 persistence.
 * No database, no EMR records — localStorage only.
 *
 * Init rule: saved layout exists → restore it; otherwise blank 30×20.
 * Reset is in-memory only and does not write or clear storage.
 */

import {
  BEHAVIOR_NONE,
  CELL_SIZE,
  CONTENT_TYPE_BLANK,
  GRID_COLUMNS,
  GRID_ROWS,
  defaultCards,
  normalizeLayout,
} from "@/lib/pageDesignerLayout";
import { emptyTableContent, sanitizeTableContent } from "@/lib/pageDesignerTable";

export const PAGE_DESIGNER_STORAGE_KEY = "emrbymd.v3.pageDesigner.layout";
export const PAGE_DESIGNER_STORAGE_VERSION = 1;

export function serializeLayout(cards) {
  return JSON.stringify({
    version: PAGE_DESIGNER_STORAGE_VERSION,
    grid: {
      columns: GRID_COLUMNS,
      rows: GRID_ROWS,
      cellSize: CELL_SIZE,
    },
    cards: (cards || []).map((card) => ({
      id: card.id,
      title: card.title,
      col: card.col,
      row: card.row,
      width: card.width,
      height: card.height,
      background: card.background,
      border: card.border,
      contentType: card.contentType || CONTENT_TYPE_BLANK,
      behavior: card.behavior || BEHAVIOR_NONE,
      rtfHtml: card.rtfHtml || "",
      tableContent: sanitizeTableContent(card.tableContent || emptyTableContent()),
      offsetX: card.offsetX || 0,
      offsetY: card.offsetY || 0,
      widthOffset: card.widthOffset || 0,
      heightOffset: card.heightOffset || 0,
    })),
  });
}

export function parseSavedLayout(raw) {
  if (!raw) return null;
  try {
    const parsed = JSON.parse(raw);
    const cards = Array.isArray(parsed) ? parsed : parsed?.cards;
    if (!cards) return null;
    return normalizeLayout(cards);
  } catch {
    return null;
  }
}

export function loadSavedLayout() {
  if (typeof localStorage === "undefined") return null;
  return parseSavedLayout(localStorage.getItem(PAGE_DESIGNER_STORAGE_KEY));
}

export function saveLayout(cards) {
  if (typeof localStorage === "undefined") return false;
  const normalized = normalizeLayout(cards);
  localStorage.setItem(PAGE_DESIGNER_STORAGE_KEY, serializeLayout(normalized));
  return true;
}

export function clearSavedLayout() {
  if (typeof localStorage === "undefined") return;
  localStorage.removeItem(PAGE_DESIGNER_STORAGE_KEY);
}

/** Blank 30×20 default. Does not change localStorage. */
export function resetLayout() {
  return defaultCards();
}

/**
 * Opening snapshot: restore the last Save, or the blank grid when nothing is saved.
 * An explicitly saved empty layout (`[]`) is distinct from "no saved layout".
 */
export function initialLayout() {
  const loaded = loadSavedLayout();
  return loaded == null ? defaultCards() : loaded;
}

export function initialSavedSnapshot() {
  const loaded = loadSavedLayout();
  if (loaded == null) return null;
  return loaded.map((card) => ({ ...card }));
}
